/**
 * <newsletter-signup-form> — wires the full newsletter signup section to the
 * Atomic ecom middleware (ArmadaApi), the same backend the return form uses.
 *
 * Mirrors the return form's plumbing: it reads its copy from
 * window.newsletterSignupStrings, posts through window.ArmadaApi (which injects
 * locale and never throws), and reuses the same status/loading/validation shape.
 *
 * Payload shape (CRM contract): { email, firstName, lastName, gender (M/F/O),
 * birthdate (YYYY-MM-DD), country (ISO), interests: string[], newsletters:
 * [{ code, subscribed, origin }] }. The visitor's checked interests are sent as
 * an array of CRM codes; consent opts the shopper into every newsletter code the
 * author configured (data-newsletters on the host element; empty = none sent).
 *
 * Markup contract:
 *   <newsletter-signup-form data-newsletters='["CODE", ...]'> — configured newsletter codes
 *   <newsletter-signup-form data-crm-origin="ecom_NewsletterPage"> — CRM signup origin
 *   form[data-newsletter-signup]      — the form
 *   [name="email"]                    — email (required)
 *   [name="first_name"] / [name="last_name"] / [name="country_code"] / [name="gender"]
 *   [name="date_of_birth"]            — optional; hidden value kept by
 *                                       <armada-date-field>'s segments, sent as ISO
 *   [name="interests"]                — checkboxes (0+)
 *   [name="accepts_marketing"]        — consent checkbox (required)
 *   button[type="submit"]             — submit button (disabled while loading)
 *   [data-newsletter-status]          — status message target
 */
class NewsletterSignupForm extends HTMLElement {
  constructor() {
    super();
    this.endpoint = this.dataset.endpoint || '/customers/newsletter';
    this.strings = window.newsletterSignupStrings || {};
    // CRM newsletter codes the author configured (data-newsletters, a JSON array
    // of the selected Newsletters list's `value` codes); consent opts the shopper
    // into all of them. No fallback — if nothing is configured, nothing is sent.
    this.newsletterCodes = this.parseNewsletters(this.dataset.newsletters);
    // `origin` tells the CRM which touchpoint produced the signup. It is
    // configured in the theme editor (newsletter form section → "CRM origin",
    // default "ecom_NewsletterPage") and reaches us as data-crm-origin.
    // Deliberately NO fallback here: the CRM-agreed value belongs in the editor
    // alone, so changing it never needs a theme deploy and the two can never
    // disagree. Clearing the setting therefore sends an empty origin.
    this.crmOrigin = (this.dataset.crmOrigin || '').trim();
    this.handleSubmit = this.handleSubmit.bind(this);
    this.form = this.querySelector('form[data-newsletter-signup]');
  }

  // Parse the configured newsletter codes from the data attribute. A missing,
  // empty, or malformed value yields an empty array (no fallback code).
  parseNewsletters(raw) {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map((c) => String(c).trim()).filter(Boolean);
      }
    } catch (_err) {
      /* ignore malformed value */
    }
    return [];
  }

  connectedCallback() {
    if (this.form) this.form.addEventListener('submit', this.handleSubmit);
  }

  disconnectedCallback() {
    if (this.form) this.form.removeEventListener('submit', this.handleSubmit);
  }

  async handleSubmit(event) {
    event.preventDefault();
    const form = this.form;
    if (form.dataset.loading === 'true') return;

    const data = new FormData(form);
    this.setStatus('', null);

    if (!this.validate(data)) return;

    if (!window.ArmadaApi) {
      this.setStatus(this.strings.error, 'error');
      return;
    }

    // country_code: prefer the chosen country, fall back to the storefront's
    // localized market so the call always carries one.
    const marketCountry =
      (window.theme && window.theme.market && window.theme.market.country_code) || '';
    const countryCode = (data.get('country_code') || '').toString() || marketCountry;

    // Checkbox values already carry the CRM interest codes (the metaobject's
    // non-translated `value`), so send them as an array — not a joined string.
    const interests = data.getAll('interests').map((v) => v.toString()).filter(Boolean);

    const payload = {
      email: (data.get('email') || '').toString().trim(),
      firstName: (data.get('first_name') || '').toString().trim(),
      lastName: (data.get('last_name') || '').toString().trim(),
      country: countryCode,
      interests,
      // Consent is required, so this is always an opt-in across every code in the
      // configured Newsletters list (empty when none is configured — no fallback).
      // Every entry carries the same `origin`.
      newsletters: this.newsletterCodes.map((code) => ({
        code,
        subscribed: true,
        origin: this.crmOrigin,
      })),
    };

    // gender: map the form's option values to the CRM's M/F/O codes. The visible
    // labels stay translatable; only these fixed codes leave the browser.
    const gender = this.toGenderCode((data.get('gender') || '').toString());
    if (gender) payload.gender = gender;

    // birthdate: optional; validated above, converted to the CRM's YYYY-MM-DD.
    const birthdate = this.readBirthdate();
    if (birthdate) payload.birthdate = birthdate;

    this.setLoading(true);

    // The CRM endpoint rejects unrecognized keys, so opt out of the client's
    // automatic `locale` injection (and we send no top-level `origin` — the only
    // `origin` in the contract is the per-entry one inside newsletters[]).
    const res = await window.ArmadaApi.post(this.endpoint, payload, { includeLocale: false });

    this.setLoading(false);

    if (res.ok) {
      this.handleSuccess();
    } else {
      this.setStatus((res.error && res.error.message) || this.strings.error, 'error');
    }
  }

  /**
   * Inline validation — focuses the first invalid field and shows a single
   * status message. Returns true when everything required is present/valid.
   */
  validate(data) {
    const email = (data.get('email') || '').toString().trim();
    if (!email) return this.fail('email', this.strings.emailRequired);
    if (!this.isValidEmail(email)) return this.fail('email', this.strings.emailInvalid);

    const required = ['first_name', 'last_name', 'country_code', 'gender'];
    for (let i = 0; i < required.length; i += 1) {
      const name = required[i];
      if (!(data.get(name) || '').toString().trim()) {
        return this.fail(name, this.strings.requiredFields);
      }
    }

    if (!data.get('accepts_marketing')) {
      return this.fail('accepts_marketing', this.strings.consentRequired);
    }

    // Birthdate is optional, but once typed it must be a real date between
    // 1900 and today.
    if (this.readBirthdate() === null) {
      return this.failBirthdate();
    }

    return true;
  }

  // '' when the field is empty, YYYY-MM-DD when it holds a real date in range,
  // null when it does not — <armada-date-field>'s own contract, returned as it
  // stands (AAS-5615).
  //
  // Nothing is re-checked here. parse() already builds the date from the day /
  // month / year segments, compares it back (Date rolls 31 April over to 1 May
  // rather than rejecting it, which is not the date that was typed) and bounds
  // it by the element's data-min / data-max — 1900-01-01 and today, rendered by
  // Liquid. A second validator over the same string only meant two places to
  // keep in step.
  //
  // Read from the segments rather than from the hidden input on purpose: it is
  // the element that fills that input, so if the element never upgraded there
  // is nothing in it to validate, and autofill lands in the segments whether or
  // not the sync has run.
  readBirthdate() {
    const field = this.form.querySelector('armada-date-field');
    return field && typeof field.parse === 'function' ? field.parse() : '';
  }

  // The date field is three segments in one box: flag them as one unit and
  // focus the first empty one (or the first, when all are filled but do not
  // make a date). Typing in any of them clears all three.
  failBirthdate() {
    const field = this.form.querySelector('armada-date-field');
    const segments = field ? Array.from(field.querySelectorAll('[data-date-part]')) : [];
    if (!segments.length) return this.fail('date_of_birth', this.strings.dobInvalid);

    this.setStatus(this.strings.dobInvalid || this.strings.error, 'error');
    const clearAll = () => segments.forEach((el) => el.removeAttribute('aria-invalid'));
    segments.forEach((el) => {
      el.setAttribute('aria-invalid', 'true');
      el.addEventListener('input', clearAll, { once: true });
    });
    const target = segments.find((el) => !el.value) || segments[0];
    if (typeof field.scrollIntoView === 'function') {
      field.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
    target.focus({ preventScroll: true });
    return false;
  }

  // Show the message, flag + reveal the offending field, and stop the submit.
  fail(name, message) {
    this.setStatus(message || this.strings.error, 'error');

    const input = this.form.querySelector(`[name="${name}"]`);
    if (input) {
      input.setAttribute('aria-invalid', 'true');
      const clear = () => input.removeAttribute('aria-invalid');
      input.addEventListener('change', clear, { once: true });
      input.addEventListener('input', clear, { once: true });

      // The checkboxes are visually hidden, so scroll their visible row into
      // view (and flag it) rather than the zero-size input itself.
      const target = input.closest('.newsletter-signup__check-row') || input;
      target.classList.add('newsletter-signup__check-row--invalid');
      input.addEventListener(
        'change',
        () => target.classList.remove('newsletter-signup__check-row--invalid'),
        { once: true }
      );
      if (typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
      if (typeof input.focus === 'function') input.focus({ preventScroll: true });
    }
    return false;
  }

  isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  // Map the gender <select> values to the CRM's M/F/O codes. Anything that
  // isn't clearly male/female (non-binary, prefer-not-to-say) becomes "O".
  toGenderCode(value) {
    const v = (value || '').toLowerCase();
    if (!v) return '';
    if (v === 'female') return 'F';
    if (v === 'male') return 'M';
    return 'O';
  }

  setLoading(isLoading) {
    this.form.dataset.loading = isLoading ? 'true' : 'false';
    const button = this.form.querySelector('button[type="submit"]');
    if (!button) return;

    button.disabled = isLoading;
    if (isLoading) {
      button.dataset.label = button.dataset.label || button.textContent;
      if (this.strings.loading) button.textContent = this.strings.loading;
    } else if (button.dataset.label) {
      button.textContent = button.dataset.label;
    }
  }

  setStatus(message, type) {
    const status = this.form.querySelector('[data-newsletter-status]');
    if (!status) return;

    status.textContent = message || '';
    status.hidden = !message;
    status.classList.toggle('newsletter-signup__status--error', type === 'error');
    status.classList.toggle('newsletter-signup__status--success', type === 'success');

    if (message && type === 'error') status.setAttribute('role', 'alert');
    else status.removeAttribute('role');

    if (message) status.focus();
  }

  // Success: wipe the form back to pristine, then confirm with a glitchy
  // thank-you (the same per-letter effect as the 404 page).
  handleSuccess() {
    this.resetFormState();
    this.renderGlitchSuccess(this.strings.success);
  }

  // Return the form to its initial, reusable state — clear values, drop every
  // validation flag, revert the date input, and re-enable the submit button.
  resetFormState() {
    this.form.reset();
    this.form.dataset.loading = 'false';

    this.form
      .querySelectorAll('[aria-invalid]')
      .forEach((el) => el.removeAttribute('aria-invalid'));
    this.form
      .querySelectorAll('.newsletter-signup__check-row--invalid')
      .forEach((el) => el.classList.remove('newsletter-signup__check-row--invalid'));

    const button = this.form.querySelector('button[type="submit"]');
    if (button) {
      button.disabled = false;
      if (button.dataset.label) {
        button.textContent = button.dataset.label;
        delete button.dataset.label;
      }
    }
  }

  // Render the success message into the status line using the 404 page's
  // <glitch-heading> element (per-letter glitch). Falls back to plain text when
  // that element/asset isn't available or motion is reduced.
  renderGlitchSuccess(message) {
    const status = this.form.querySelector('[data-newsletter-status]');
    if (!status) return;

    if (!customElements.get('glitch-heading')) {
      this.setStatus(message, 'success');
      return;
    }

    status.textContent = '';
    status.hidden = !message;
    status.classList.remove('newsletter-signup__status--error');
    status.classList.toggle('newsletter-signup__status--success', !!message);
    status.removeAttribute('role');

    // Accessible copy for the aria-live region — a single, plain string so
    // screen readers announce the message rather than spelling out each
    // glitched letter.
    const sr = document.createElement('span');
    sr.className = 'visually-hidden';
    sr.textContent = message || '';
    status.appendChild(sr);

    // Visual-only glitch; hidden from assistive tech (the sr span covers it).
    const heading = document.createElement('glitch-heading');
    heading.className = 'newsletter-signup__glitch';
    heading.setAttribute('aria-hidden', 'true');
    const text = document.createElement('span');
    text.className = 'newsletter-signup__glitch-text';
    text.setAttribute('data-glitch-text', '');
    text.textContent = message || '';
    heading.appendChild(text);

    // Appending to an in-document node upgrades the element and fires its
    // connectedCallback, which kicks off the glitch loop.
    status.appendChild(heading);
    status.focus();
  }
}

if (!customElements.get('newsletter-signup-form')) {
  customElements.define('newsletter-signup-form', NewsletterSignupForm);
}
